import { ref } from 'vue';
import { useSnackbarHandler } from '@/composables/useSnackbarHandler';
import {
  getVendors as getVendorsApi,
  createVendor as createVendorApi,
} from '@/services/liens/LienService';
import type { VendorData } from '@/models/lienTypes';

const SEARCH_DEBOUNCE_MS = 300;

/** Debounced vendor search + inline "create new vendor" flow, extracted from `LienFormPanel.vue`'s
 *  pattern for reuse here. Vendors are a generic resource, so this reuses `LienService` directly. */
export function useVendorSearch() {
  const { handleError, handleSuccess } = useSnackbarHandler();

  const vendors = ref<VendorData[]>([]);
  const vendorsLoading = ref(false);
  const searchTerm = ref('');
  const vendorError = ref('');

  const showAddVendor = ref(false);
  const newVendorName = ref('');
  const creatingVendor = ref(false);

  let searchTimer: ReturnType<typeof setTimeout> | null = null;

  function search(term: string): void {
    searchTerm.value = term;
    if (searchTimer) clearTimeout(searchTimer);
    vendorError.value = '';
    searchTimer = setTimeout(async () => {
      vendorsLoading.value = true;
      try {
        vendors.value = await getVendorsApi(term);
      } catch {
        vendorError.value = 'Unable to load vendors — check QuickBooks connection in Settings.';
      } finally {
        vendorsLoading.value = false;
      }
    }, SEARCH_DEBOUNCE_MS);
  }

  /** Seeds the list with a single known vendor (e.g. an already-selected row) without a network call. */
  function seed(vendor: VendorData): void {
    vendors.value = [vendor];
  }

  function openAddVendor(): void {
    newVendorName.value = searchTerm.value;
    showAddVendor.value = true;
  }

  function cancelAddVendor(): void {
    showAddVendor.value = false;
    newVendorName.value = '';
  }

  async function saveNewVendor(): Promise<VendorData | null> {
    const name = newVendorName.value.trim();
    if (!name) return null;
    creatingVendor.value = true;
    try {
      const vendor = await createVendorApi({ displayName: name });
      vendors.value = [...vendors.value, vendor];
      cancelAddVendor();
      handleSuccess(`Vendor "${vendor.displayName}" created`);
      return vendor;
    } catch (e: unknown) {
      handleError(e);
      return null;
    } finally {
      creatingVendor.value = false;
    }
  }

  function reset(): void {
    if (searchTimer) clearTimeout(searchTimer);
    vendors.value = [];
    vendorsLoading.value = false;
    searchTerm.value = '';
    vendorError.value = '';
    cancelAddVendor();
  }

  return {
    vendors,
    vendorsLoading,
    searchTerm,
    vendorError,
    showAddVendor,
    newVendorName,
    creatingVendor,
    search,
    seed,
    openAddVendor,
    cancelAddVendor,
    saveNewVendor,
    reset,
  };
}
